// RTK Query endpoints for the reporting-api backend.
//
// All 8 endpoints existed before the Angular→React conversion — the Angular app
// hit the same backend via HttpClient + RxJS shareReplay. We reuse them directly.
// No new API endpoints were needed for the React reporting tool.
//
// Every endpoint takes a mandatory `organisationId` query-string param that
// the backend uses to restrict results to a single org. The front-end hooks
// (in @frontend/react) fetch the id from `useSelectedOrganisationId` and
// forward it here; they also skip the query while the id is still
// resolving, so `organisationId` is guaranteed non-empty by the time a
// request reaches the network. RTK Query cache keys fold the id in via
// `providesTags` so switching orgs invalidates cleanly.
//
// Cache tags enable automatic invalidation when navigating between sessions or
// players. Each tag is scoped by the relevant ID so switching sessions doesn't
// cause a full cache flush.
import { reportingApi } from '../../modules/servers/api'
import {
  AllPlayersSessionSummaryResponse,
  PlayerActionsResponse,
  PlayerSessionSummaryResponse,
  ReportingPlayer,
  ReportingSession,
  ReportingTeam,
  TeamSessionSummaryResponse
} from './types'

// Builds the query string for reporting endpoints. `organisationId` is
// mandatory and always emitted first for log-consistency; optional extras
// (periodId, position, …) are appended when truthy.
function buildReportingQuery(
  path: string,
  organisationId: string,
  extras?: Record<string, string | undefined>
): string {
  const params = new URLSearchParams()
  params.set('organisationId', organisationId)
  if (extras) {
    for (const [k, v] of Object.entries(extras)) {
      if (v) params.set(k, v)
    }
  }
  return `${path}?${params.toString()}`
}

export const reportingEndpoints = reportingApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /teams - list all teams user has access to
    getReportingTeams: builder.query<
      ReportingTeam[],
      { organisationId: string }
    >({
      query: ({ organisationId }) =>
        buildReportingQuery('/teams', organisationId),
      providesTags: (result, error, { organisationId }) => [
        { type: 'reportingTeams', id: organisationId }
      ]
    }),

    // GET /teams/{teamId}/players - list players for a team
    getTeamPlayers: builder.query<
      ReportingPlayer[],
      { teamId: string; organisationId: string }
    >({
      query: ({ teamId, organisationId }) =>
        buildReportingQuery(`/teams/${teamId}/players`, organisationId),
      providesTags: (result, error, { teamId, organisationId }) => [
        { type: 'reportingPlayers', id: `${teamId}-${organisationId}` }
      ]
    }),

    // GET /teams/{teamId}/sessions - list sessions for a team
    getTeamSessions: builder.query<
      ReportingSession[],
      { teamId: string; organisationId: string }
    >({
      query: ({ teamId, organisationId }) =>
        buildReportingQuery(`/teams/${teamId}/sessions`, organisationId),
      providesTags: (result, error, { teamId, organisationId }) => [
        { type: 'reportingSessions', id: `${teamId}-${organisationId}` }
      ]
    }),

    // GET /teams/{teamId}/sessions/{sessionId}/players - list players in a session
    getSessionPlayers: builder.query<
      ReportingPlayer[],
      { teamId: string; sessionId: string; organisationId: string }
    >({
      query: ({ teamId, sessionId, organisationId }) =>
        buildReportingQuery(
          `/teams/${teamId}/sessions/${sessionId}/players`,
          organisationId
        ),
      providesTags: (result, error, { sessionId, organisationId }) => [
        { type: 'reportingPlayers', id: `${sessionId}-${organisationId}` }
      ]
    }),

    // GET /teams/{teamId}/sessions/{sessionId}/summary - team stats for session
    getTeamSessionSummary: builder.query<
      TeamSessionSummaryResponse,
      { teamId: string; sessionId: string; organisationId: string }
    >({
      query: ({ teamId, sessionId, organisationId }) =>
        buildReportingQuery(
          `/teams/${teamId}/sessions/${sessionId}/summary`,
          organisationId
        ),
      providesTags: (result, error, { sessionId, organisationId }) => [
        { type: 'sessionSummary', id: `${sessionId}-${organisationId}` }
      ]
    }),

    // GET /teams/{teamId}/sessions/{sessionId}/players/summary - all players stats
    getAllPlayersSessionSummary: builder.query<
      AllPlayersSessionSummaryResponse,
      {
        teamId: string
        sessionId: string
        organisationId: string
        periodId?: string
        position?: string
      }
    >({
      query: ({ teamId, sessionId, organisationId, periodId, position }) =>
        buildReportingQuery(
          `/teams/${teamId}/sessions/${sessionId}/players/summary`,
          organisationId,
          { periodId, position }
        ),
      providesTags: (
        result,
        error,
        { sessionId, periodId, position, organisationId }
      ) => [
        {
          type: 'playerSummary',
          id: `all-${sessionId}-${periodId ?? 'all'}-${position ?? 'all'}-${organisationId}`
        }
      ]
    }),

    // GET /teams/{teamId}/sessions/{sessionId}/players/{playerId}/summary - single player stats
    getPlayerSessionSummary: builder.query<
      PlayerSessionSummaryResponse,
      {
        teamId: string
        sessionId: string
        playerId: string
        organisationId: string
      }
    >({
      query: ({ teamId, sessionId, playerId, organisationId }) =>
        buildReportingQuery(
          `/teams/${teamId}/sessions/${sessionId}/players/${playerId}/summary`,
          organisationId
        ),
      providesTags: (
        result,
        error,
        { playerId, sessionId, organisationId }
      ) => [
        {
          type: 'playerSummary',
          id: `${playerId}-${sessionId}-${organisationId}`
        }
      ]
    }),

    // GET /teams/{teamId}/sessions/{sessionId}/players/{playerId}/actions - player actions
    getPlayerActions: builder.query<
      PlayerActionsResponse,
      {
        teamId: string
        sessionId: string
        playerId: string
        organisationId: string
      }
    >({
      query: ({ teamId, sessionId, playerId, organisationId }) =>
        buildReportingQuery(
          `/teams/${teamId}/sessions/${sessionId}/players/${playerId}/actions`,
          organisationId
        ),
      providesTags: (
        result,
        error,
        { playerId, sessionId, organisationId }
      ) => [
        {
          type: 'playerActions',
          id: `${playerId}-${sessionId}-${organisationId}`
        }
      ]
    })
  })
})

export const {
  useGetReportingTeamsQuery,
  useLazyGetReportingTeamsQuery,
  useGetTeamPlayersQuery,
  useLazyGetTeamPlayersQuery,
  useGetTeamSessionsQuery,
  useLazyGetTeamSessionsQuery,
  useGetSessionPlayersQuery,
  useLazyGetSessionPlayersQuery,
  useGetTeamSessionSummaryQuery,
  useLazyGetTeamSessionSummaryQuery,
  useGetAllPlayersSessionSummaryQuery,
  useLazyGetAllPlayersSessionSummaryQuery,
  useGetPlayerSessionSummaryQuery,
  useLazyGetPlayerSessionSummaryQuery,
  useGetPlayerActionsQuery,
  useLazyGetPlayerActionsQuery
} = reportingEndpoints
